import versionsJson from "@generated/versions.json";

import type {
  AvailabilityMap,
  BundledSpec,
  Json,
  NavService,
  OperationEntry,
  ServiceEntry,
  SnippetBundle,
  VersionEntry,
  VersionManifest,
} from "./types.ts";

/**
 * Versioned content registry.
 *
 * Everything is discovered by glob rather than hand-registered, so adding a
 * version means running `pnpm version:new` and nothing else -- there is no
 * import list here to forget to update.
 *
 * Split by cost: the navigation tree and availability map are small and needed
 * for the first paint, so they load eagerly. The full specification and the code
 * snippets are large and only endpoint pages need them, so they are fetched on
 * demand.
 */
const navModules = import.meta.glob<NavService[]>("/src/generated/nav.*.json", {
  eager: true,
  import: "default",
});

const availabilityModules = import.meta.glob<AvailabilityMap>(
  "/src/generated/availability.*.json",
  { eager: true, import: "default" },
);

const specLoaders = import.meta.glob<BundledSpec>(
  "/src/generated/spec.*.json",
  {
    import: "default",
  },
);

const snippetLoaders = import.meta.glob<SnippetBundle>(
  "/src/generated/snippets.*.json",
  { import: "default" },
);

const keyed = <T>(
  modules: Record<string, T>,
  prefix: string,
): Record<string, T> =>
  Object.fromEntries(
    Object.entries(modules).flatMap(([path, value]) => {
      const match = new RegExp(`/${prefix}\\.(.+)\\.json$`).exec(path);
      return match ? [[match[1], value] as const] : [];
    }),
  );

const NAV = keyed(navModules, "nav");
const AVAILABILITY = keyed(availabilityModules, "availability");
const SPEC_LOADERS = keyed(specLoaders, "spec");
const SNIPPET_LOADERS = keyed(snippetLoaders, "snippets");

export const manifest = versionsJson as VersionManifest;

const missing = manifest.versions.filter((v) => !NAV[v.id]);
if (missing.length > 0) {
  throw new Error(
    `versions.json declares ${missing.map((v) => v.id).join(", ")} but no ` +
      `generated content exists for them. Run \`pnpm content\`.`,
  );
}

export const DEFAULT_VERSION = manifest.default;

export const isKnownVersion = (id: string | undefined): boolean =>
  Boolean(id && NAV[id]);

export function getVersionEntry(versionId: string): VersionEntry | undefined {
  return manifest.versions.find((v) => v.id === versionId);
}

/** Nav tree and availability map — always resident. */
export function getBundle(versionId: string): {
  nav: NavService[];
  availability: AvailabilityMap;
} {
  const nav = NAV[versionId];
  if (!nav) throw new Error(`Unknown docs version "${versionId}"`);
  return { nav, availability: AVAILABILITY[versionId] ?? {} };
}

/* -------------------------------------------------------------------------- */
/* Specification                                                               */
/* -------------------------------------------------------------------------- */

const specCache = new Map<string, Promise<BundledSpec>>();
const loadedSpecs = new Map<string, BundledSpec>();

export function loadSpec(versionId: string): Promise<BundledSpec> {
  const cached = specCache.get(versionId);
  if (cached) return cached;

  const loader = SPEC_LOADERS[versionId];
  if (!loader) throw new Error(`No specification for version "${versionId}"`);

  const promise = loader().then((spec) => {
    // Cached synchronously so deref() and the lookups below can stay sync once
    // a page has suspended on the load.
    loadedSpecs.set(versionId, spec);
    return spec;
  });
  specCache.set(versionId, promise);
  return promise;
}

/**
 * The already-loaded specification.
 *
 * Only valid after `loadSpec` has resolved for this version. Every caller sits
 * beneath a component that suspended on it.
 */
export function getSpec(versionId: string): BundledSpec {
  const spec = loadedSpecs.get(versionId);
  if (!spec) {
    throw new Error(
      `Specification for "${versionId}" is not loaded. Await loadSpec() first.`,
    );
  }
  return spec;
}

export function loadSnippets(
  versionId: string,
  serviceId: string,
): Promise<SnippetBundle> {
  const loader = SNIPPET_LOADERS[`${versionId}.${serviceId}`];
  if (!loader) {
    throw new Error(`No snippets for "${versionId}.${serviceId}"`);
  }
  return loader();
}

/* -------------------------------------------------------------------------- */
/* Lookups                                                                     */
/* -------------------------------------------------------------------------- */

export function findOperation(
  versionId: string,
  operationId: string,
): OperationEntry | undefined {
  return getSpec(versionId).operations.find((o) => o.id === operationId);
}

export function findService(
  versionId: string,
  serviceId: string,
): ServiceEntry | undefined {
  return getSpec(versionId).services.find((s) => s.id === serviceId);
}

/**
 * Resolves a local `$ref` against the bundled components.
 *
 * Returns the node untouched when there is nothing to resolve, so callers can
 * pass any schema node through unconditionally.
 */
export function deref(versionId: string, node: unknown): Json | undefined {
  if (node === null || typeof node !== "object") return undefined;

  const ref = (node as Json).$ref;
  if (typeof ref !== "string") return node as Json;
  if (!ref.startsWith("#/components/")) return undefined;

  const parts = ref.replace("#/components/", "").split("/");
  let cursor: unknown = getSpec(versionId).components;
  for (const part of parts) {
    if (cursor === null || typeof cursor !== "object") return undefined;
    cursor = (cursor as Json)[part];
  }
  return deref(versionId, cursor);
}

/** The component name behind a `$ref`, for display next to a resolved schema. */
export function refName(node: unknown): string | undefined {
  if (node === null || typeof node !== "object") return undefined;
  const ref = (node as Json).$ref;
  if (typeof ref !== "string") return undefined;
  return ref.split("/").pop();
}

/* -------------------------------------------------------------------------- */
/* Routing helpers                                                             */
/* -------------------------------------------------------------------------- */

export const operationHref = (versionId: string, operationId: string): string =>
  `/${versionId}/reference/${operationId}`;

export const guideHref = (versionId: string, slug: string): string =>
  `/${versionId}/guides/${slug}`;
